/**
 * User model for mobile client
 * Represents the user data structure used in the mobile application
 */
import {defaultUserController} from "../controllers/UserController";
import Profile from "./Profile_b";
import Settings from "./Settings_b";
import { images } from "../modules/Resources";

class User {
  constructor({
    id = null,
    username = '',
    email = '',
    password = '',
    role = 'user',
    status = 'active',
    providerId = null,
    provider = 'email',
    lastOnline = null,
    isPremium = false,
    isEmailVerified = false,
    isPhoneVerified = false,
    phoneNumber = null,
    resetToken = null,
    resetTokenExpiry = null,
    tokenVersion = 0,
    createdAt = null,
    // Trust score fields
    trustScore = 20,
    trustTierLabel = null,
    // Premium settings
    premiumSettings = null,
    // Admin-specific fields
    adminLevel = null,
    lastAdminAction = null,
    totalActionsPerformed = 0,
    // Moderator-specific fields
    position = null,
    lastModeratorAction = null,
    totalModerationsPerformed = 0,
    specialization = null,
  } = {}) {
    this.id = id;
    this.username = username;
    this.email = email;
    this.password = password;
    this.role = role;
    this.status = status;
    this.providerId = providerId;
    this.provider = provider;
    this.lastOnline = lastOnline ? new Date(lastOnline) : new Date();
    this.isPremium = isPremium;
    this.isEmailVerified = isEmailVerified;
    this.isPhoneVerified = isPhoneVerified;
    this.phoneNumber = phoneNumber;
    this.resetToken = resetToken;
    this.resetTokenExpiry = resetTokenExpiry ? new Date(resetTokenExpiry) : null;
    this.tokenVersion = tokenVersion;
    this.createdAt = createdAt ? new Date(createdAt) : null;

    // Trust score fields
    this.trustScore = trustScore;
    this.trustTierLabel = trustTierLabel;

    // Premium settings
    this.premiumSettings = premiumSettings;

    // Admin-specific fields
    this.adminLevel = adminLevel;
    this.lastAdminAction = lastAdminAction ? new Date(lastAdminAction) : null;
    this.totalActionsPerformed = totalActionsPerformed;

    // Moderator-specific fields
    this.position = position;
    this.lastModeratorAction = lastModeratorAction ? new Date(lastModeratorAction) : null;
    this.totalModerationsPerformed = totalModerationsPerformed;
    this.specialization = specialization;

    this.profile  = null;
    this.is = new Is();
  }

  /**
   * Check if user has administrator role
   * @returns {boolean} True if user is an administrator
   */
  get isAdmin() {
    return this.role === 'administrator';
  }

  /**
   * Factory method to create a User instance from API response
   * @param {Object} data - Raw user data from API
   * @returns {User} - Instantiated User object
   */
  static fromAPI(data) {
    return new User({
      id: data.id,
      username: data.username,
      email: data.email,
      password: data.password,
      role: data.role,
      status: data.status,
      providerId: data.providerId,
      provider: data.provider,
      lastOnline: data.lastOnline,
      isPremium: data.isPremium,
      isEmailVerified: data.isEmailVerified,
      isPhoneVerified: data.isPhoneVerified,
      phoneNumber: data.phoneNumber,
      resetToken: data.resetToken,
      resetTokenExpiry: data.resetTokenExpiry,
      tokenVersion: data.tokenVersion,
    });
  }

  applyFromAPI(data) {
    if (!data) return;
    
    this.id = data.id;
    this.username = data.username;
    this.email = data.email;
    this.password = data.password;
    this.role = data.role;
    this.status = data.status;
    this.providerId = data.providerId;
    this.provider = data.provider;
    this.lastOnline = data.lastOnline ? new Date(data.lastOnline) : new Date();
    this.isPremium = data.isPremium;
    this.isEmailVerified = data.isEmailVerified;
    this.isPhoneVerified = data.isPhoneVerified;
    this.phoneNumber = data.phoneNumber;
    this.resetToken = data.resetToken;
    this.resetTokenExpiry = data.resetTokenExpiry ? new Date(data.resetTokenExpiry) : null;
    this.tokenVersion = data.tokenVersion;
    this.createdAt = data.createdAt ? new Date(data.createdAt) : null;

    // Trust score fields
    if (data.trustScore !== undefined) this.trustScore = data.trustScore;
    if (data.trustTierLabel !== undefined) this.trustTierLabel = data.trustTierLabel;

    // Premium settings (passed separately from the profile response)
    if (data.premiumSettings !== undefined) this.premiumSettings = data.premiumSettings;

    // Admin-specific fields
    if (data.adminLevel !== undefined) this.adminLevel = data.adminLevel;
    if (data.lastAdminAction) this.lastAdminAction = new Date(data.lastAdminAction);
    if (data.totalActionsPerformed !== undefined) this.totalActionsPerformed = data.totalActionsPerformed;

    // Moderator-specific fields
    if (data.position !== undefined) this.position = data.position;
    if (data.lastModeratorAction) this.lastModeratorAction = new Date(data.lastModeratorAction);
    if (data.totalModerationsPerformed !== undefined) this.totalModerationsPerformed = data.totalModerationsPerformed;
    if (data.specialization !== undefined) this.specialization = data.specialization;
  }

   static fromData(data, additional) {
    const NEWUSER = new User();
    const user = data.user;
    const profile = data.profile;

    NEWUSER.profile = Profile.fromAPI(profile);

    NEWUSER.applyFromAPI(user);  

    NEWUSER.settings = Settings.fromAPI(data.settings);

    if (data.premiumSettings) NEWUSER.premiumSettings = data.premiumSettings;

    NEWUSER.is = Is.fromData(additional);

    return NEWUSER;
  }

  static fromObject(data) {
    if (!data) return null;
    
    const NEWUSER = new User();
    
    NEWUSER.applyFromAPI(data);

    NEWUSER.profile = Profile.fromAPI(data.profile);

    NEWUSER.settings = Settings.fromAPI(data.settings);

    return NEWUSER;
  }


  /**
   * Convert User instance to plain object (for storage, etc.)
   * @returns {Object} Plain JavaScript object
   */
  toJSON() {
    return {
      id: this.id,
      username: this.username,
      email: this.email,
      password: this.password,
      role: this.role,
      status: this.status,
      providerId: this.providerId,
      provider: this.provider,
      lastOnline: this.lastOnline?.toISOString() || null,
      isPremium: this.isPremium,
      isEmailVerified: this.isEmailVerified,
      isPhoneVerified: this.isPhoneVerified,
      phoneNumber: this.phoneNumber,
      resetToken: this.resetToken,
      resetTokenExpiry: this.resetTokenExpiry ? this.resetTokenExpiry.toISOString() : null,
      tokenVersion: this.tokenVersion,
    };
  }

  update(data) {
    return defaultUserController.updateUser(this.id, data);
  }

  getDisplayName() {
    return this.profile?.displayName || this.username;
  }

  getAvatar() {
    // Return string URL for web, or object with uri for mobile
    const avatarUrl = this.profile?.currentAvatar?.url;
    if (avatarUrl) {
      // Check if we're in a web environment (for admin panel)
      return typeof window !== 'undefined' && window.document ? avatarUrl : { uri: avatarUrl };
    }
    // Return default avatar URL string for web
    return typeof window !== 'undefined' && window.document ? null : images.default_avatar;
  }

  getBanner() {
    return this.profile?.currentBanner?.url ? { uri: this.profile.currentBanner?.url } : images.default_banner;
  }
}

class Is {
  constructor() {
    this.blocked = false;
    this.muted = false;
    this.reported = false;
    this.followed = false;
  }

  static fromData(data) {
    if (!data) return new Is();

    const NEWIS = new Is();

    NEWIS.blocked = data.blocked;
    NEWIS.muted = data.muted;
    NEWIS.reported = data.reported;
    NEWIS.followed = data.follow;

    return NEWIS;
  }
}

export default User;